import {
    useState
} from "react";
import {
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Stepper,
    Button,
    Badge,
    Card,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    ChevronLeft,
    Wallet,
    Share,
    Check,
    Plane
} from "lucide-react-native";
import {
    Path,
    Svg
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

const ChevronLeftIcon = createKitIcon(ChevronLeft);

const ShareIcon = createKitIcon(Share);

const CheckIcon = createKitIcon(Check);

const WalletIcon = createKitIcon(Wallet);

const GRID = 25;

const FINDER = 8;

const QR_SEED = 20418;

const QR_FILL_RATIO = 0.48;

const FINDER_ORIGINS = [
    {
        x: 0,
        y: 0
    },
    {
        x: GRID - 7,
        y: 0
    },
    {
        y: GRID - 7,
        x: 0
    }
];

const getSquare = (x: number, y: number, size: number) => `M${x},${y}h${size}v${size}h-${size}z`;

const isFinder = (x: number, y: number) => (x < FINDER && y < FINDER) || (x > GRID - FINDER - 1 && y < FINDER) || (x < FINDER && y > GRID - FINDER - 1);

const buildQrPath = () => {
    let seed = QR_SEED;

    let path = "";

    for(let y = 0; y < GRID; y++) {
        for(let x = 0; x < GRID; x++) {
            if(isFinder(x, y)) {
                continue;
            }

            seed = (seed * 1103515245 + 12345) % 2147483648;

            if(seed / 2147483648 < QR_FILL_RATIO) {
                path += getSquare(x, y, 1);
            }
        }
    }

    FINDER_ORIGINS.forEach((origin) => {
        path += getSquare(origin.x, origin.y, 7) + getSquare(origin.x + 1, origin.y + 1, 5) + getSquare(origin.x + 2, origin.y + 2, 3);
    });

    return path;
};

const QR_PATH = buildQrPath();

const FACTS = [
    {
        value: "B12",
        key: "gate"
    },
    {
        value: "14C",
        key: "seat"
    },
    {
        key: "boarding",
        value: "08:35"
    },
    {
        key: "group",
        value: "3"
    }
];

const TRIP_STEPS = [
    {
        key: "checkIn",
        time: "06:50"
    },
    {
        key: "security",
        time: "07:45"
    },
    {
        key: "boarding",
        time: "08:35"
    },
    {
        key: "departure",
        time: "09:05"
    }
];

const ACTIVE_STEP = 1;

const FLIGHT_DATE = new Date(2026, 10, 12);

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    split: {
        justifyContent: "space-between",
        flexDirection: "row"
    },
    plane: {
        transform: [
            {
                rotate: "45deg"
            }
        ]
    },
    cities: {
        alignItems: "flex-end",
        flexDirection: "row"
    },
    column: {
        flexDirection: "column"
    },
    end: {
        alignItems: "flex-end"
    },
    dashed: {
        borderStyle: "dashed"
    },
    dotted: {
        borderStyle: "dotted"
    },
    notchRight: {
        position: "absolute",
        right: 0
    },
    notchLeft: {
        position: "absolute",
        left: 0
    },
    stub: {
        alignItems: "center",
        flexDirection: "row"
    },
    middle: {
        alignItems: "center",
        flexDirection: "row"
    },
    screen: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    },
    facts: {
        flexDirection: "row"
    },
    center: {
        textAlign: "center"
    },
    fact: {
        minWidth: 0,
        flex: 1
    },
    grow: {
        minWidth: 0,
        flex: 1
    },
    perforation: {
        height: 0
    },
    page: {
        flex: 1
    }
});

const handlePress = () => undefined;

const Travel01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        avatarSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isInWallet,
        setIsInWallet
    ] = useState(false);

    const dateFormat = new Intl.DateTimeFormat(activeLocale, {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric"
    });

    const handleWallet = () => setIsInWallet(!isInWallet);

    const notchSize = spaces.spacingLg;

    const renderNotch = (isLeft: boolean) => <View
        style={[
            isLeft ? stylesheet.notchLeft : stylesheet.notchRight,
            {
                backgroundColor: colors.content.container.default,
                marginRight: isLeft ? undefined : -notchSize / 2,
                marginLeft: isLeft ? -notchSize / 2 : undefined,
                borderRadius: radiuses.full,
                marginTop: -notchSize / 2,
                height: notchSize,
                width: notchSize
            }
        ]}
    />;

    const renderFact = (fact: typeof FACTS[number]) => <View
        style={stylesheet.fact}
        key={fact.key}
    >
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t(`blocks.travel01.facts.${fact.key}`)}
        </Text>
        <Text
            variant="titleLargeSize"
            color="high"
        >
            {fact.value}
        </Text>
    </View>;

    return <View
        style={stylesheet.page}
    >
        <ScrollView
            contentContainerStyle={[
                stylesheet.screen,
                {
                    maxWidth: shapeConfigs.sheetMaxWidth,
                    padding: spaces.spacingMd,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={stylesheet.header}
            >
                <Button
                    accessibilityLabel={t("blocks.travel01.back")}
                    icon={ChevronLeftIcon}
                    onPress={handlePress}
                    variant="ghost"
                    type="neutral"
                />
                <Text
                    variant="titleMediumSize"
                    role="heading"
                    color="high"
                >
                    {t("blocks.travel01.title")}
                </Text>
                <Button
                    accessibilityLabel={t("blocks.travel01.share")}
                    onPress={handlePress}
                    icon={ShareIcon}
                    variant="ghost"
                    type="neutral"
                />
            </View>
            <Card
                accessibilityLabel={t("blocks.travel01.ticketLabel")}
                variant="outline"
            >
                <View
                    style={[
                        stylesheet.column,
                        {
                            padding: spaces.spacingLg,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.split}
                    >
                        <Badge
                            title={t("blocks.travel01.checkedIn")}
                            icon={CheckIcon}
                            variant="tonal"
                            type="success"
                        />
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {dateFormat.format(FLIGHT_DATE)}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.cities,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <View>
                            <Text
                                variant="displayMediumSize"
                                color="high"
                            >
                                IST
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.travel01.from")}
                            </Text>
                        </View>
                        <View
                            style={[
                                stylesheet.grow,
                                {
                                    paddingBottom: spaces.spacingXl,
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.middle,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <View
                                    style={[
                                        stylesheet.grow,
                                        stylesheet.dotted,
                                        {
                                            borderTopColor: colors.content.icon.low,
                                            borderTopWidth: borders.separator * 2
                                        }
                                    ]}
                                />
                                <Icon
                                    style={stylesheet.plane}
                                    color="high"
                                    icon={Plane}
                                />
                                <View
                                    style={[
                                        stylesheet.grow,
                                        stylesheet.dotted,
                                        {
                                            borderTopColor: colors.content.icon.low,
                                            borderTopWidth: borders.separator * 2
                                        }
                                    ]}
                                />
                            </View>
                            <Text
                                style={stylesheet.center}
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.travel01.duration")}
                            </Text>
                        </View>
                        <View
                            style={stylesheet.end}
                        >
                            <Text
                                variant="displayMediumSize"
                                color="high"
                            >
                                AYT
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.travel01.to")}
                            </Text>
                        </View>
                    </View>
                    <View
                        style={stylesheet.split}
                    >
                        <View>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.travel01.departure")}
                            </Text>
                            <Text
                                variant="titleLargeSize"
                                color="high"
                            >
                                09:05
                            </Text>
                        </View>
                        <View
                            style={stylesheet.end}
                        >
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.travel01.arrival")}
                            </Text>
                            <Text
                                variant="titleLargeSize"
                                color="high"
                            >
                                10:35
                            </Text>
                        </View>
                    </View>
                    <View
                        style={[
                            stylesheet.split,
                            {
                                alignItems: "flex-end"
                            }
                        ]}
                    >
                        <View>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.travel01.passenger")}
                            </Text>
                            <Text
                                variant="titleMediumSize"
                                color="high"
                            >
                                {t("blocks.travel01.passengerName")}
                            </Text>
                        </View>
                        <View
                            style={stylesheet.end}
                        >
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.travel01.flight")}
                            </Text>
                            <Text
                                variant="monoMediumSize"
                                color="high"
                            >
                                TK2412
                            </Text>
                        </View>
                    </View>
                    <View
                        style={[
                            stylesheet.facts,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        {FACTS.map(renderFact)}
                    </View>
                </View>
                <View
                    style={stylesheet.perforation}
                >
                    {renderNotch(true)}
                    {renderNotch(false)}
                    <View
                        style={[
                            stylesheet.dashed,
                            {
                                borderTopColor: colors.content.border.default,
                                borderTopWidth: borders.separator * 2,
                                marginHorizontal: spaces.spacingLg
                            }
                        ]}
                    />
                </View>
                <View
                    style={[
                        stylesheet.stub,
                        {
                            padding: spaces.spacingLg,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={{
                            backgroundColor: colors.content.container.default,
                            borderColor: colors.content.border.default,
                            borderWidth: borders.separator,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingSm
                        }}
                    >
                        <Svg
                            accessibilityLabel={t("blocks.travel01.qrLabel")}
                            height={avatarSizes.xLarge + spaces.spacingSm}
                            width={avatarSizes.xLarge + spaces.spacingSm}
                            viewBox={`0 0 ${GRID} ${GRID}`}
                        >
                            <Path
                                fill={colors.content.text.high}
                                fillRule="evenodd"
                                d={QR_PATH}
                            />
                        </Svg>
                    </View>
                    <View
                        style={[
                            stylesheet.grow,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <View>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.travel01.reservation")}
                            </Text>
                            <Text
                                variant="monoMediumSize"
                                color="high"
                            >
                                8KQ4ZM
                            </Text>
                        </View>
                        <View>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t("blocks.travel01.baggage")}
                            </Text>
                            <Text
                                variant="labelMediumSize"
                                color="high"
                            >
                                {t("blocks.travel01.baggageValue")}
                            </Text>
                        </View>
                        <Text
                            variant="bodySmallSize"
                            color="mid"
                        >
                            {t("blocks.travel01.scanHint")}
                        </Text>
                    </View>
                </View>
            </Card>
            <Button
                title={t(isInWallet ? "blocks.travel01.inWallet" : "blocks.travel01.addToWallet")}
                icon={isInWallet ? CheckIcon : WalletIcon}
                spreadBehaviour="stretch"
                onPress={handleWallet}
                variant="outline"
                type="neutral"
            />
            <View
                style={[
                    stylesheet.column,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                    role="heading"
                    color="high"
                >
                    {t("blocks.travel01.plan")}
                </Text>
                <Stepper
                    steps={TRIP_STEPS.map((step) => ({
                        description: `${step.time} · ${t(`blocks.travel01.steps.${step.key}.note`)}`,
                        title: t(`blocks.travel01.steps.${step.key}.title`),
                        key: step.key
                    }))}
                    accessibilityLabel={t("blocks.travel01.plan")}
                    activeIndex={ACTIVE_STEP}
                    orientation="vertical"
                    size="small"
                />
            </View>
        </ScrollView>
    </View>;
};
export default Travel01;
